import {
  defaultPostureConfig,
  historyMessages,
  placementGuide,
  privacyNotice,
  reportMessages,
  settingsMessages,
  storageMessages,
  type AppConfigOverrides,
} from "@studystraight/shared-config";
import type { CameraPlacement, PostureIssueType, TimerPreset } from "@studystraight/shared-types";
import type { SessionRepository } from "@studystraight/session-core";
import { useState } from "react";

import { isIssueMeasurable, TIMER_PRESETS, type AppSettings } from "../../app/settings.js";

const PLACEMENTS: readonly CameraPlacement[] = ["FRONTAL", "SIDE"];

/** Các issue đo bằng delta góc — cùng shape `DeltaThreshold` nên chung một editor. */
const DELTA_ISSUES = [
  { key: "headDown", issue: "HEAD_DOWN", label: reportMessages.issueLabels.HEAD_DOWN },
  {
    key: "leaningForward",
    issue: "LEANING_FORWARD",
    label: reportMessages.issueLabels.LEANING_FORWARD,
  },
  { key: "shoulderTilt", issue: "SHOULDER_TILT", label: reportMessages.issueLabels.SHOULDER_TILT },
] as const satisfies readonly { key: string; issue: PostureIssueType; label: string }[];

type DeltaKey = (typeof DELTA_ISSUES)[number]["key"];
type ThresholdKey = DeltaKey | "tooClose";

/**
 * Ghi một ngưỡng vào `AppConfigOverrides`.
 *
 * `value === null` = xoá override (trả field đó về default) chứ không phải ghi 0: một ngưỡng 0 độ
 * nghĩa là "báo động ngay khi lệch một chút", trong khi người dùng chỉ muốn bỏ tuỳ chỉnh.
 */
function patchThreshold(
  base: AppConfigOverrides,
  key: ThresholdKey,
  field: string,
  value: number | null,
): AppConfigOverrides {
  const posture: Record<string, unknown> = { ...base.posture };
  const entry: Record<string, number> = { ...(posture[key] as Record<string, number> | undefined) };
  if (value === null) delete entry[field];
  else entry[field] = value;
  posture[key] = entry;
  // Cast một lần tại đây: `DeepPartial<PostureConfig>` không mô tả được phép ghi động theo key,
  // và `resolveConfig` đã coi mọi nhánh override là dữ liệu người dùng có thể thiếu field.
  return { ...base, posture: posture as NonNullable<AppConfigOverrides["posture"]> };
}

function readThreshold(overrides: AppConfigOverrides, key: ThresholdKey, field: string): number | null {
  const entry = (overrides.posture as Record<string, Record<string, number>> | undefined)?.[key];
  const value = entry?.[field];
  return typeof value === "number" ? value : null;
}

interface ThresholdInputProps {
  label: string;
  unit: string;
  /** Giá trị mặc định của app — hiện làm placeholder để biết đang lệch bao nhiêu so với gốc. */
  fallback: number;
  value: number | null;
  step: number;
  onChange: (value: number | null) => void;
}

function ThresholdInput({
  label,
  unit,
  fallback,
  value,
  step,
  onChange,
}: ThresholdInputProps): React.JSX.Element {
  return (
    <label className="field">
      <span className="field__label">
        {label} ({unit})
      </span>
      <input
        className="field__input"
        type="number"
        inputMode="decimal"
        step={step}
        min={0}
        placeholder={String(fallback)}
        value={value === null ? "" : value}
        onChange={(event) => {
          const next = Number.parseFloat(event.target.value);
          onChange(Number.isFinite(next) ? next : null);
        }}
      />
    </label>
  );
}

export interface SettingsScreenProps {
  settings: AppSettings;
  onChange: (settings: AppSettings) => void;
  repository: SessionRepository | null;
  persistent: boolean;
  onBack: () => void;
}

/** §23, §25, §27 — cài đặt + section Developer để tune ngưỡng (§34 Phase 12). */
export function SettingsScreen({
  settings,
  onChange,
  repository,
  persistent,
  onBack,
}: SettingsScreenProps): React.JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const [deleted, setDeleted] = useState(false);

  const placement = settings.placement;
  const overrides = settings.overrides[placement];
  const defaults = defaultPostureConfig[placement];

  const setOverrides = (next: AppConfigOverrides): void => {
    onChange({ ...settings, overrides: { ...settings.overrides, [placement]: next } });
  };

  const setPreset = (preset: TimerPreset): void => onChange({ ...settings, timerPreset: preset });

  const deleteAll = (): void => {
    if (repository === null) return;
    void repository.deleteAll().then(() => {
      setConfirming(false);
      setDeleted(true);
    });
  };

  return (
    <main className="screen">
      <header className="topbar">
        <button type="button" className="btn btn--ghost" onClick={onBack}>
          ← Quay lại
        </button>
        <h2 className="topbar__title">{settingsMessages.title}</h2>
      </header>

      <div className="settings">
        <section>
          <h3 className="section-title">{settingsMessages.placement}</h3>
          <div className="placement__cards">
            {PLACEMENTS.map((option) => (
              <button
                key={option}
                type="button"
                className={`card${placement === option ? " card--selected" : ""}`}
                aria-pressed={placement === option}
                onClick={() => onChange({ ...settings, placement: option })}
              >
                <span className="card__title">{placementGuide[option].label}</span>
                <span className="card__hint">{placementGuide[option].hint}</span>
              </button>
            ))}
          </div>
          {/* Quyết định #17: baseline gắn với một placement cụ thể, nên đổi ở đây KHÔNG sửa phiên
              đang chạy — nói rõ ra thay vì để người dùng tưởng nó có hiệu lực ngay. */}
          <p className="hint">{settingsMessages.placementHint}</p>
        </section>

        <section>
          <h3 className="section-title">{settingsMessages.timer}</h3>
          <div className="chips">
            {TIMER_PRESETS.map((preset) => (
              <button
                key={String(preset)}
                type="button"
                className={`chip${settings.timerPreset === preset ? " chip--selected" : ""}`}
                aria-pressed={settings.timerPreset === preset}
                onClick={() => setPreset(preset)}
              >
                {preset === "custom" ? settingsMessages.custom : `${preset} phút`}
              </button>
            ))}
          </div>
          {settings.timerPreset === "custom" ? (
            <label className="field">
              <span className="field__label">{settingsMessages.customUnit}</span>
              <input
                className="field__input"
                type="number"
                inputMode="numeric"
                min={5}
                max={180}
                value={settings.customDurationMinutes}
                onChange={(event) => {
                  const next = Number.parseInt(event.target.value, 10);
                  onChange({
                    ...settings,
                    customDurationMinutes: Number.isFinite(next) ? next : 0,
                  });
                }}
              />
            </label>
          ) : null}
        </section>

        <section className="toggles">
          <label className="toggle">
            <input
              type="checkbox"
              checked={settings.voiceEnabled}
              onChange={(event) => onChange({ ...settings, voiceEnabled: event.target.checked })}
            />
            <span>{settingsMessages.voice}</span>
          </label>
          <label className="toggle">
            <input
              type="checkbox"
              checked={settings.eyeBreakEnabled}
              onChange={(event) => onChange({ ...settings, eyeBreakEnabled: event.target.checked })}
            />
            <span>{settingsMessages.eyeBreak}</span>
          </label>
          <label className="toggle">
            <input
              type="checkbox"
              checked={settings.debugMode}
              onChange={(event) => onChange({ ...settings, debugMode: event.target.checked })}
            />
            <span>{settingsMessages.debug}</span>
          </label>
          <p className="hint">{settingsMessages.debugHint}</p>
        </section>

        {/* §34 Phase 12: mọi giá trị tune được trên thiết bị thật phải sửa được ngay tại thiết bị,
            không phải build lại app — đây là lý do `resolveConfig` nhận `overrides`. */}
        <section>
          <h3 className="section-title">{settingsMessages.developer}</h3>
          <p className="hint">{settingsMessages.developerHint}</p>

          {/* Ẩn hẳn ô ngưỡng của issue không đo được ở placement này (SIDE không đo lệch vai):
              một ô nhập có tác dụng bằng 0 gây hiểu sai rằng ngưỡng đã được tune. */}
          {DELTA_ISSUES.filter((issue) => isIssueMeasurable(placement, issue.issue)).map((issue) => (
            <div className="field__group" key={issue.key}>
              <span className="field__group-title">{issue.label}</span>
              <ThresholdInput
                label={settingsMessages.warningDelta}
                unit={settingsMessages.degrees}
                step={1}
                fallback={defaults[issue.key].warningDelta}
                value={readThreshold(overrides, issue.key, "warningDelta")}
                onChange={(value) =>
                  setOverrides(patchThreshold(overrides, issue.key, "warningDelta", value))
                }
              />
              <ThresholdInput
                label={settingsMessages.badDelta}
                unit={settingsMessages.degrees}
                step={1}
                fallback={defaults[issue.key].badDelta}
                value={readThreshold(overrides, issue.key, "badDelta")}
                onChange={(value) =>
                  setOverrides(patchThreshold(overrides, issue.key, "badDelta", value))
                }
              />
            </div>
          ))}

          <div className="field__group">
            <span className="field__group-title">{reportMessages.issueLabels.TOO_CLOSE}</span>
            <ThresholdInput
              label={settingsMessages.warningDelta}
              unit={settingsMessages.ratio}
              step={0.05}
              fallback={defaults.tooClose.warningRatio}
              value={readThreshold(overrides, "tooClose", "warningRatio")}
              onChange={(value) =>
                setOverrides(patchThreshold(overrides, "tooClose", "warningRatio", value))
              }
            />
            <ThresholdInput
              label={settingsMessages.badDelta}
              unit={settingsMessages.ratio}
              step={0.05}
              fallback={defaults.tooClose.badRatio}
              value={readThreshold(overrides, "tooClose", "badRatio")}
              onChange={(value) =>
                setOverrides(patchThreshold(overrides, "tooClose", "badRatio", value))
              }
            />
          </div>

          <button type="button" className="btn btn--ghost" onClick={() => setOverrides({})}>
            {settingsMessages.resetOverrides}
          </button>
        </section>

        <section>
          <h3 className="section-title">{settingsMessages.data}</h3>
          {persistent ? null : <p className="hint">{storageMessages.unavailable}</p>}
          {deleted ? <p className="hint">{historyMessages.deleted}</p> : null}
          {confirming ? (
            <div className="banner" role="alertdialog">
              <span>{storageMessages.deleteAllConfirm}</span>
              <div className="actions">
                <button
                  type="button"
                  className="btn btn--secondary"
                  onClick={() => setConfirming(false)}
                >
                  Giữ lại
                </button>
                <button type="button" className="btn btn--danger" onClick={deleteAll}>
                  Xoá
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="btn btn--ghost"
              disabled={repository === null}
              onClick={() => setConfirming(true)}
            >
              {historyMessages.deleteAll}
            </button>
          )}
          <p className="privacy">{privacyNotice}</p>
        </section>
      </div>
    </main>
  );
}
